import { useState } from "react";
import type { AppSettings, MediaRecord } from "../types/app";
import { nowIso } from "../constants";
import { createId } from "../utils/id";
import { validateMediaFile } from "../utils/fileValidation";

interface Props {
  settings: AppSettings;
  onSave: (patch: Partial<AppSettings>) => Promise<void>;
  onMedia: (record: MediaRecord) => Promise<void>;
}

export function SetupPage({ settings, onSave, onMedia }: Props) {
  const [form, setForm] = useState(settings);
  const [notice, setNotice] = useState("");
  return (
    <main className="screen">
      <h1>初回設定</h1>
      <section className="panel">
        <p>これはAIとの音声会話アプリです。会話時には必要な発言と選択された記憶がOpenAI APIへ送信されます。</p>
        <p>会話履歴、記憶、設定、メディアはこの端末のIndexedDBに保存します。サーバー側データベースは使いません。</p>
        <label>
          ユーザー名
          <input data-testid="setup-user-name" value={form.userName} onChange={(event) => setForm({ ...form, userName: event.target.value })} />
        </label>
        <label>
          呼ばれ方
          <input data-testid="setup-call-name" value={form.userCallName} onChange={(event) => setForm({ ...form, userCallName: event.target.value })} />
        </label>
        <label>
          キャラクター名
          <input value={form.characterName} onChange={(event) => setForm({ ...form, characterName: event.target.value })} />
        </label>
        <label>
          会話スタイル
          <textarea value={form.conversationStyle} onChange={(event) => setForm({ ...form, conversationStyle: event.target.value })} />
        </label>
        <label>
          画像または動画
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
            onChange={async (event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              const type = file.type.startsWith("video/") ? "video" : "image";
              const valid = await validateMediaFile(file, type);
              if (!valid.ok) {
                setNotice(valid.reason);
                return;
              }
              const id = createId("media");
              await onMedia({ id, type, filename: file.name, mimeType: file.type, blob: file, state: "idle", createdAt: nowIso() });
              setForm({ ...form, selectedCharacterMediaId: id });
            }}
          />
        </label>
        <button
          type="button"
          data-testid="setup-submit"
          onClick={async () => {
            await Promise.race([
              navigator.mediaDevices?.getUserMedia({ audio: true }).then((stream) => stream.getTracks().forEach((track) => track.stop())),
              new Promise((resolve) => window.setTimeout(resolve, 1200)),
            ]).catch(() => setNotice("マイクが許可されていないため、テキスト入力で続行できます。"));
            await onSave({
              ...form,
              userName: form.userName.trim() || "ユーザー",
              userCallName: form.userCallName.trim() || "あなた",
              updatedAt: nowIso(),
            });
          }}
        >
          設定して始める
        </button>
        <p>{notice}</p>
      </section>
    </main>
  );
}
